/**
 * Dadware commerce core — dw-commerce.js (deferred, no dependencies beyond Dawn's
 * pubsub/constants when present). Shared by the product spotlight, the mobile
 * purchase bar and the multi-buy section.
 *
 *  DW.config            Parsed #dw-config JSON (offers, money sample, strings)
 *  DW.formatMoney(c)    Formats cents in the visitor's presentment currency
 *  DW.setTotals(p,b)    Set totals from a product's Multi-buy metafields
 *  DW.addToCart(items)  One request for a whole set; drawer + badge + events
 *  DW.track(name,data)  Custom analytics event (never duplicates standard ones)
 *
 * Pure helpers are exposed on DW.pure for unit tests (tools/tests).
 */
(function (root) {
  'use strict';

  const DW = (root.DW = root.DW || {});

  /* ---------------------------------------------------------------- money */
  // The layout renders `{{ 100000000 | money }}` (1,000,000.00 in the
  // presentment currency). Reading the separators from that sample keeps JS
  // formatting identical to Liquid for every market/currency.
  function parseMoneySample(sample) {
    const text = String(sample || '').replace(/<[^>]*>/g, '').trim();
    const m = text.match(/1([^\d]?)000([^\d]?)000(?:([^\d])(\d{2}))?/);
    if (!m) return null;
    return {
      prefix: text.slice(0, m.index),
      suffix: text.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[3] || '',
      decimals: m[4] ? 2 : 0,
    };
  }

  function formatMoney(cents, cfg) {
    const value = Math.round(Number(cents) || 0);
    const negative = value < 0;
    const abs = Math.abs(value);
    // No parsable sample: plain number, never a guessed currency symbol.
    if (!cfg) return (negative ? '-' : '') + (abs / 100).toFixed(2);
    const whole = cfg.decimals ? Math.floor(abs / 100) : Math.round(abs / 100);
    const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, cfg.thousands);
    const fraction = cfg.decimals ? cfg.decimal + String(abs % 100).padStart(2, '0') : '';
    return (negative ? '-' : '') + cfg.prefix + grouped + fraction + cfg.suffix;
  }

  /* ------------------------------------------------------------ set prices */
  // bundle: {enabled, total2, total3} from the product's Multi-buy metafields
  // (final set prices in cents). A set price applies only when the set is
  // exactly that size, every item has the same unit price, and the set price
  // is below the plain total. Otherwise the plain total is used.
  function setTotals(unitPrices, bundle) {
    const prices = (unitPrices || []).map((p) => Math.max(0, Math.round(Number(p) || 0)));
    const count = prices.length;
    const subtotal = prices.reduce((sum, p) => sum + p, 0);
    const b = bundle || {};
    const configured = count === 2 ? Number(b.total2) : count === 3 ? Number(b.total3) : 0;
    const uniform = prices.every((p) => p === prices[0]);
    let total = subtotal;
    if (b.enabled && uniform && configured > 0 && configured < subtotal) total = Math.round(configured);
    return { subtotal, discount: subtotal - total, total, count, each: count ? Math.round(total / count) : 0 };
  }

  // Groups identical variants so the cart gets one line per variant.
  function mergeItems(items) {
    const map = new Map();
    (items || []).forEach((item) => {
      const id = String(item.id);
      const qty = item.quantity == null ? 1 : Math.floor(Number(item.quantity));
      if (!id || id === 'undefined' || id === 'null' || !(qty > 0)) return;
      const key = id + '|' + JSON.stringify(item.properties || {});
      const existing = map.get(key);
      if (existing) existing.quantity += qty;
      else map.set(key, { id, quantity: qty, properties: item.properties });
    });
    return [...map.values()];
  }

  DW.pure = { parseMoneySample, formatMoney, setTotals, mergeItems };

  /* --------------------------------------------------------------- config */
  function readConfig() {
    const el = root.document && root.document.getElementById('dw-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  DW.config = readConfig();
  DW.money = parseMoneySample(DW.config.moneySample);
  DW.formatMoney = (cents) => formatMoney(cents, DW.money);
  DW.setTotals = setTotals;
  DW.string = (key, vars) => {
    let s = (DW.config.strings && DW.config.strings[key]) || key;
    Object.entries(vars || {}).forEach(([k, v]) => {
      s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}|\\[' + k + '\\]', 'g'), () => String(v));
    });
    return s;
  };

  /* ------------------------------------------------------------ analytics */
  // Custom events only. Shopify's standard events (product_viewed,
  // product_added_to_cart, cart_viewed, checkout_started, checkout_completed)
  // keep coming from Shopify and Dawn's StandardEvents wiring.
  DW.track = (name, payload) => {
    try {
      if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
        root.Shopify.analytics.publish('dadware:' + name, payload || {});
      }
    } catch (e) {
      /* never block commerce on analytics */
    }
    root.document.dispatchEvent(new CustomEvent('dw:' + name, { detail: payload || {} }));
  };

  /* ----------------------------------------------------------------- cart */
  function linesEvent(target, items) {
    const SE = root.StandardEvents;
    if (!SE || !SE.CartLinesUpdateEvent || !target) return null;
    const deferred = SE.CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(
      new SE.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  function resolveLines(deferred) {
    if (!deferred) return;
    const SE = root.StandardEvents;
    const fetchCart =
      typeof root.CartItems !== 'undefined' && root.CartItems.fetchCartData
        ? root.CartItems.fetchCartData()
        : fetch(root.routes.cart_url + '.json').then((r) => r.json());
    fetchCart
      .then((cart) => {
        if (!cart || !cart.currency) return deferred.reject(new Error('Missing cart currency'));
        deferred.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      })
      .catch((e) => deferred.reject(e));
  }

  DW.addToCart = async function addToCart(rawItems, options) {
    const opts = options || {};
    const items = mergeItems(rawItems);
    if (!items.length) throw new Error(DW.string('error_generic'));
    const drawer = root.document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = root.location.pathname;
    }
    const deferred = linesEvent(opts.eventTarget, items);

    let response;
    let data = {};
    try {
      response = await fetch(root.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      data = await response.json().catch(() => ({}));
    } catch (networkError) {
      if (deferred) deferred.reject(networkError);
      const err = new Error(DW.string('error_generic'));
      err.code = 'NETWORK';
      throw err;
    }

    if (!response.ok || data.status) {
      const message = data.description || data.message || DW.string('error_generic');
      if (typeof root.publish === 'function' && root.PUB_SUB_EVENTS) {
        root.publish(root.PUB_SUB_EVENTS.cartError, { source: opts.source || 'dadware', errors: data.errors || message, message });
      }
      if (deferred) deferred.reject(new Error(message));
      const err = new Error(message);
      err.code = data.status || response.status;
      throw err;
    }

    resolveLines(deferred);

    if (typeof root.publish === 'function' && root.PUB_SUB_EVENTS) {
      root.publish(root.PUB_SUB_EVENTS.cartUpdate, { source: opts.source || 'dadware', cartData: data });
    }

    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else if (!opts.stay) {
      root.location.href = root.routes.cart_url;
    }
    return data;
  };
})(typeof window !== 'undefined' ? window : globalThis);
